import {
    Colors,
    FOCUS_RING,
    StepBadge,
    Typography,
    STEP_RAIL_LAYOUT,
    stepRailActiveScale,
    stepRailBadgeTransition,
    stepRailHoverLift,
    stepRailLabelTransition,
} from '@amelio/ui-library';

import { getComputedStatus, getVariant } from '../utils/StepItemUtils';

import type { Step } from '../StepItem';
import type { StepperSize } from '../types';
import type { ColorType } from '@amelio/ui-library';

interface StepRailItemProps {
    step: Step;
    index: number;
    activeIndex: number;
    size: StepperSize;
    isClickable: boolean;
    rankLabel?: string;
    onStepClick?: (step: Step, index: number) => void;
    getStepAriaLabel?: (step: Step, index: number) => string;
    stepRef: (el: HTMLLIElement | null) => void;
    dataTestId?: string;
}

const getRankColor = ({ computedStatus }: { computedStatus: Step['status'] }): ColorType => {
    if (computedStatus === 'active') {
        return Colors.STEP_RAIL_VIOLET;
    }
    if (computedStatus === 'completed') {
        return Colors.STEP_RAIL_TURQUOISE;
    }
    return Colors.STEP_RAIL_IDLE;
};

const getNameColor = ({ computedStatus }: { computedStatus: Step['status'] }): ColorType => {
    if (computedStatus === 'active') {
        return Colors.SLATE;
    }
    if (computedStatus === 'completed') {
        return Colors.SLATE_80;
    }
    return Colors.STEP_RAIL_MUTED;
};

export const StepRailItem = ({
    step,
    index,
    activeIndex,
    size,
    isClickable,
    rankLabel,
    onStepClick,
    getStepAriaLabel,
    stepRef,
    dataTestId,
}: StepRailItemProps) => {
    const computedStatus = getComputedStatus({ step, index, activeIndex });
    const isCurrent = index === activeIndex;

    const dimmedClasses = step.disabled ? 'opacity-50' : '';

    const handleClick = () => {
        if (isClickable && onStepClick) {
            onStepClick(step, index);
        }
    };

    return (
        <li
            ref={stepRef}
            className={`${STEP_RAIL_LAYOUT.columnMin} flex-1`}
            data-testid={dataTestId}
        >
            <button
                type={'button'}
                onClick={handleClick}
                disabled={!isClickable}
                aria-current={isCurrent ? 'step' : undefined}
                aria-label={
                    getStepAriaLabel
                        ? getStepAriaLabel(step, index)
                        : `Step ${index + 1}: ${step.label}`
                }
                className={`${FOCUS_RING} ${STEP_RAIL_LAYOUT.columnPaddingX} ${STEP_RAIL_LAYOUT.columnPaddingBottom} ${STEP_RAIL_LAYOUT.columnRadius} group flex w-full flex-col items-center text-center ${
                    isClickable
                        ? stepRailHoverLift
                        : `${stepRailBadgeTransition} cursor-not-allowed`
                }`}
            >
                <div
                    className={`${dimmedClasses} ${stepRailBadgeTransition} ${
                        isCurrent ? stepRailActiveScale : ''
                    }`}
                >
                    <StepBadge
                        step={index + 1}
                        variant={getVariant({ computedStatus })}
                        size={size}
                        appearance={'ring'}
                    />
                </div>

                <div
                    className={`${STEP_RAIL_LAYOUT.labelTop} ${STEP_RAIL_LAYOUT.labelGap} flex flex-col items-center ${dimmedClasses}`}
                >
                    {rankLabel && (
                        <Typography
                            variant={'step-rail-rank'}
                            align={'center'}
                            color={getRankColor({ computedStatus })}
                            className={stepRailLabelTransition}
                        >
                            {rankLabel}
                        </Typography>
                    )}
                    <Typography
                        variant={'step-rail-name'}
                        align={'center'}
                        color={getNameColor({ computedStatus })}
                        truncate={2}
                        className={`text-wrap ${stepRailLabelTransition}`}
                    >
                        {step.label}
                    </Typography>
                </div>
            </button>
        </li>
    );
};

export type { StepRailItemProps };
